import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown } from "antd";
import { DownOutlined, MenuOutlined, LogoutOutlined, EnvironmentFilled } from "@ant-design/icons";
import { message } from "antd";
import logo from "../../assets/img/logo.png";
import default_image from "../../assets/img/default_image_profile.jpg";

// ─── Modal ────────────────────────────────────────────────────────────────────
import LocationPickerView from "./LocationPickerView";

// ─── Services ─────────────────────────────────────────────────────────────────
import LocationService from "../../services/api/LocationService";
import SearchService from "../../services/api/SearchService";
import AuthService from "../../services/api/AuthService";

// ─── Constants ────────────────────────────────────────────────────────────────
const locStyle = { color: "#FDB415", fontSize: 20 };
const locationService = new LocationService();
const searchService   = new SearchService();

const hideCenter = ["/login", "/signup", "/reset-password", "/forgot-password"];
const isExcludedPath = ["/privacy-notice", "/about", "/contact-us"];

// ─── Props ────────────────────────────────────────────────────────────────────
interface HeaderViewProps {
  onChangeLocation: (locationId: string) => void;
}

const HeaderView = ({ onChangeLocation }: HeaderViewProps) => {
  const navigate        = useNavigate();
  const { pathname }    = useLocation();

  const [option,          setOption]          = useState("");
  const [locations,       setLocations]       = useState<any[]>([]);
  const [user,            setUser]            = useState<any>();
  const [currentActive,   setCurrentActive]   = useState<string[]>([]);
  const [isLocationModal, setIsLocationModal] = useState(false);
  const [mobileMenuOpen,  setMobileMenuOpen]  = useState(false);

  // AP-5857: on mobile the open nav dropdown detaches from the header and
  // flickers over the page content while scrolling on iOS Safari. Close it the
  // moment the user starts scrolling. `touchmove` fires before the `scroll`
  // event during iOS momentum scroll, so closing there removes the menu before
  // it can visibly jump; `scroll` (capture phase, to also catch nested scroll
  // containers) and `wheel` cover trackpad / non-touch scrolling.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const close = () => setMobileMenuOpen(false);
    window.addEventListener("touchmove", close, { passive: true });
    window.addEventListener("scroll", close, { passive: true, capture: true });
    window.addEventListener("wheel", close, { passive: true });
    return () => {
      window.removeEventListener("touchmove", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("wheel", close);
    };
  }, [mobileMenuOpen]);

  // ─── Sync user from localStorage on pathname change ────────────────────────
  useEffect(() => {
    const userString = localStorage.getItem("user") ?? "{}";
    try {
      setUser(JSON.parse(userString));
    } catch {
      setUser({});
    }
  }, [pathname]);

  // ─── Track active menu item ────────────────────────────────────────────────
  useEffect(() => {
    if (!isExcludedPath.includes(pathname)) {
      setCurrentActive([pathname.replace("/", "")]);
    }
  }, [pathname]);

  // ─── Initialize on mount ───────────────────────────────────────────────────
  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    const savedLocation = localStorage.getItem("location") ?? "";
    const path          = pathname;

    let locationResult = await locationService.getAllLocations();
    locationResult = (locationResult || []).sort((a: any, b: any) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
    setLocations(locationResult);

    // Respect a location the user has already chosen — don't let geolocation
    // silently override it on page refresh (AP-5869).
    if (savedLocation) {
      const saved = (locationResult || []).find((o: any) => o._id === savedLocation);
      if (saved) {
        setOption(saved.name);
        onChangeLocation(saved._id);
        if (path.includes("/restaurants/")) {
          checkRestaurant();
        }
        return;
      }
    }

    await navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat  = position.coords.latitude;
        const long = position.coords.longitude;

        if (
          lat.toFixed(6)  !== import.meta.env.VITE_DEFAULT_LAT &&
          long.toFixed(6) !== import.meta.env.VITE_DEFAULT_LONG
        ) {
          const result = await locationService.getLocation(lat, long);
          if (result) {
            localStorage.setItem("location", result._id);
            onChangeLocation(result._id);
            if (savedLocation !== result._id) {
              if (path.includes("/restaurants/")) {
                checkRestaurant();
              } else if (path.includes("/order-history")) {
                console.log("history");
              } else {
                setOption(result.name);
                if (localStorage.getItem("access_token") && !isExcludedPath.includes(pathname)) {
                  navigate("/");
                }
              }
            } else {
              if (path.includes("/restaurants/")) {
                checkRestaurant();
              } else if (path.includes("/order-history")) {
                console.log("history");
              } else {
                setOption(result.name);
                if (localStorage.getItem("access_token") && !isExcludedPath.includes(pathname)) {
                  navigate("/");
                }
              }
            }
            onChangeLocation(result._id);
          } else {
            message.error("Sorry! Your location could not be determined.", 2);
            const fallback = await locationService.getLocation(0, 0);
            if (fallback) {
              setOption(fallback.name);
              localStorage.setItem("location", fallback._id);
              if (savedLocation !== fallback._id) {
                if (localStorage.getItem("access_token") && !isExcludedPath.includes(pathname)) {
                  navigate("/");
                }
              } else {
                checkRestaurant();
              }
              onChangeLocation(fallback._id);
            } else {
              setOption(fallback?.name ?? "");
            }
          }
        } else {
          message.error("Sorry! Your location could not be determined.", 2);
          const fallback = await locationService.getLocation(0, 0);
          if (fallback) {
            setOption(fallback.name);
            localStorage.setItem("location", fallback._id);
            if (savedLocation !== fallback._id) {
              if (localStorage.getItem("access_token") && !isExcludedPath.includes(pathname)) {
                navigate("/");
              }
            } else {
              checkRestaurant();
            }
            onChangeLocation(fallback._id);
          } else {
            setOption(fallback?.name ?? "");
          }
        }
      },
      async () => {
        message.error("Sorry! Your location could not be determined.", 2);
        const fallback = await locationService.getLocation(0, 0);
        if (fallback) {
          setOption(fallback.name);
          localStorage.setItem("location", fallback._id);
          if (savedLocation !== fallback._id) {
            if (path.includes("/restaurants/")) {
              checkRestaurant();
            } else if (path.includes("/order-history")) {
              console.log("history");
            } else {
              if (localStorage.getItem("access_token") && !isExcludedPath.includes(pathname)) {
                navigate("/");
              }
            }
          } else {
            checkRestaurant();
          }
          onChangeLocation(fallback._id);
        }
      },
      { enableHighAccuracy: true, timeout: 1000, maximumAge: 0 }
    );
  };

  const checkRestaurant = async () => {
    const path = pathname;
    if (path.includes("/restaurants/")) {
      const slug = path.replace("/restaurants/", "");
      if (slug) {
        const storeSlug = await searchService.getStoreId(slug, 1);
        const storeId   = storeSlug?.storeId ?? "";
        const result    = await searchService.getStoreNew(storeId);
        if (result?.location?._id) {
          setOption(result.location.name);
          localStorage.setItem("location", result.location._id);
          onChangeLocation(result.location._id);
        }
      }
    }
  };

  const onSelect = (val: any) => {
    const savedLocation = localStorage.getItem("location") ?? "";
    const result = locations.find((o: any) => o._id === val);
    if (result) {
      setOption(result.name);
      localStorage.setItem("location", result._id);
      onChangeLocation(result._id);
      if (savedLocation !== val && !isExcludedPath.includes(pathname)) {
        navigate("/");
      }
    }
    setIsLocationModal(false);
  };

  const handleClick = (path: string) => {
    navigate(path);
  };

  const onClickLocationPicker = (status: boolean) => {
    setIsLocationModal(status);
  };

  const onLogout = async () => {
    const refreshToken = localStorage.getItem("refresh_token");
    if (refreshToken) {
      try {
        await new AuthService().logout(refreshToken);
      } catch {
        // proceed with local logout regardless of server response
      }
    }
    [
      "user", "access_token", "refresh_token", "resto_id",
      "items", "reservation_details", "store_name", "delivery_address",
      "store_geolocation", "reservation_fee", "delivery_fee", "driver_tip",
      "birth_info", "promo_code", "availability_id", "type", "time", "date",
      "ebike_delivery", "fblst_1329329546066039",
    ].forEach((key) => localStorage.removeItem(key));
    navigate("/");
  };

  // ─── Menus ────────────────────────────────────────────────────────────────
  const menuWebItems = [
    {
      key: "profile",
      label: <a onClick={() => handleClick("/profile")}>Profile</a>,
    },
    {
      key: "order-history",
      label: <a onClick={() => handleClick("/order-history")}>Order History</a>,
    },
    {
      key: "logout",
      label: (
        <span onClick={onLogout}>
          <LogoutOutlined />&nbsp;Log out
        </span>
      ),
    },
  ];

  const mobileMenuItems = [
    {
      key: "about",
      label: <a onClick={() => handleClick("/about")}>ABOUT</a>,
      className: "mobile-menu-border",
    },
    {
      key: "driver",
      label: <a onClick={() => handleClick("/driver-registration")}>Become a Driver Partner with appétit</a>,
      className: "mobile-menu-border",
    },
    ...(localStorage.getItem("access_token")
      ? [
          {
            key: "profile",
            label: <a onClick={() => handleClick("/profile")} className="order-history">PROFILE</a>,
            className: "mobile-menu-border",
          },
          {
            key: "order-history",
            label: <a onClick={() => handleClick("/order-history")} className="order-history">ORDER HISTORY</a>,
            className: "mobile-menu-border",
          },
          {
            key: "logout",
            label: <a onClick={onLogout} className="order-history">LOG OUT</a>,
            className: "mobile-menu-border",
          },
        ]
      : [
          {
            key: "login",
            label: <a href="/login">LOG IN</a>,
            className: "mobile-menu-border",
          },
          {
            key: "signup",
            label: <a href="/signup">SIGN UP</a>,
            className: "mobile-menu-border",
          },
        ]),
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  const isLoginOrSignup = pathname === "/login" || pathname === "/signup";

  return (
    <div className="stickyHeader">
      {/* ── Web header ── */}
      <div className="header_web">
        <div className="header">
          <div className="header__left">
            <div className="header_tab" />
            <div className="header__logo">
              <a onClick={() => handleClick("/")}>
                <div className="logo_img">
                  <img src={logo} alt="appetit logo" />
                </div>
              </a>
              <div />
            </div>
            <div className="header_tab">
              <a onClick={() => handleClick("/about")}>About</a>
            </div>
            <div className="header_tab">
              <a onClick={() => handleClick("/driver-registration")}>Become a Driver Partner with appétit</a>
            </div>
          </div>

          {!hideCenter.includes(pathname) && (
            <div className="header__center">
              <div className="header_tab">
                <EnvironmentFilled style={locStyle} />
              </div>
              <div className="header_tab">
                <span className="ant-dropdown-link" onClick={() => onClickLocationPicker(true)}>
                  {option}
                  <DownOutlined className="header_icon" />
                </span>
              </div>
            </div>
          )}

          {localStorage.getItem("access_token") ? (
            <div className="header__right">
              <div className="user_tab">
                <div className="user_image_frame">
                  <img
                    src={user?.image ? user.image.url : default_image}
                    alt={user?.image?.name ? user.image.name.substr(0, 10) : "default_image"}
                  />
                </div>
                <Dropdown menu={{ items: menuWebItems, selectedKeys: currentActive }}>
                  <span className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
                    <MenuOutlined />
                  </span>
                </Dropdown>
              </div>
            </div>
          ) : (
            <div className="header__right">
              <div className={pathname === "/login" ? "header_tab active" : "header_tab"}>
                <a href="/login">Log In</a>
              </div>
              {isLoginOrSignup ? (
                <div className={pathname === "/signup" ? "header_tab active" : "header_tab"}>
                  <a href="/signup">Sign Up</a>
                </div>
              ) : (
                <div className="header_tab">
                  <a href="/signup">
                    <div className="header_button">Sign Up</div>
                  </a>
                </div>
              )}
              <div className="header_tab" />
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile header ── */}
      <div className="header_mobile">
        <div className="header">
          <div className="header__left">
            <div className="header_tab" style={{ zIndex: 99 }}>
              <Dropdown
                menu={{ items: mobileMenuItems }}
                trigger={["click"]}
                open={mobileMenuOpen}
                onOpenChange={setMobileMenuOpen}
              >
                <span
                  style={{ marginTop: "5px" }}
                  className="ant-dropdown-link"
                  onClick={(e) => e.preventDefault()}
                >
                  <MenuOutlined style={{ fontSize: 20 }} />
                </span>
              </Dropdown>
            </div>
            {/* TEMPORARY SCALE FOR NOW */}
            <div className="header__logo" style={{ transform: "scale(1.7)" }}>
              <a onClick={() => handleClick("/")}>
                <div className="logo_img">
                  <img src={logo} alt="appetit logo" />
                </div>
              </a>
            </div>
          </div>

          {!hideCenter.includes(pathname) && (
            <div className="header__center" style={{ zIndex: 99 }}>
              <div className="header_tab">
                <EnvironmentFilled style={locStyle} />
              </div>
              <div className="header_tab">
                <span className="ant-dropdown-link" onClick={() => onClickLocationPicker(true)}>
                  {option}
                  <DownOutlined className="header_icon" />
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <LocationPickerView
        visible={isLocationModal}
        onClickLocation={onSelect}
        onClose={onClickLocationPicker}
        activeLocation={option}
        locations={locations}
      />
    </div>
  );
};

export default HeaderView;